import React from 'react';
import { Alert, AlertTitle, Box, useTheme } from '@mui/material';
import { CheckCircle, Error, Warning, Info } from '@mui/icons-material';
import { useAppTheme } from '@contexts/ThemeContext';

interface CustomAlertProps {
    type: 'success' | 'error' | 'warning' | 'info';
    title?: string;
    message: string;
    sx?: object;
}

export const CustomAlert: React.FC<CustomAlertProps> = ({
    type,
    title,
    message,
    sx = {},
}) => {
    const { isDarkMode } = useAppTheme();
    const theme = useTheme();

    const getAlertStyles = () => {
        const baseStyles = {
            borderRadius: '8px',
            border: '1px solid',
            '& .MuiAlert-icon': {
                fontSize: '20px',
            },
            '& .MuiAlert-message': {
                padding: 0,
            },
        };

        switch (type) {
            case 'success':
                return {
                    ...baseStyles,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(92, 184, 92, 0.15)'
                        : 'rgba(46, 125, 50, 0.1)',
                    border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(92, 184, 92, 0.5)'
                        : '1px solid rgba(46, 125, 50, 0.3)',
                    color: theme.palette.text.primary,
                    '& .MuiAlert-icon': {
                        color: theme.palette.success?.main || '#4caf50',
                    },
                };

            case 'error':
                return {
                    ...baseStyles,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(217, 83, 79, 0.15)'
                        : 'rgba(244, 67, 54, 0.1)',
                    border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(217, 83, 79, 0.5)'
                        : '1px solid rgba(244, 67, 54, 0.3)',
                    color: theme.palette.text.primary,
                    '& .MuiAlert-icon': {
                        color: theme.palette.error?.main || '#f44336',
                    },
                };

            case 'warning':
                return {
                    ...baseStyles,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(240, 173, 78, 0.15)'
                        : 'rgba(255, 152, 0, 0.1)',
                    border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(240, 173, 78, 0.5)'
                        : '1px solid rgba(255, 152, 0, 0.3)',
                    color: theme.palette.text.primary,
                    '& .MuiAlert-icon': {
                        color: theme.palette.warning?.main || '#ff9800',
                    },
                };

            case 'info':
                return {
                    ...baseStyles,
                    backgroundColor: theme.palette.mode === 'dark'
                        ? 'rgba(91, 192, 222, 0.15)'
                        : 'rgba(33, 150, 243, 0.1)',
                    border: theme.palette.mode === 'dark'
                        ? '1px solid rgba(91, 192, 222, 0.5)'
                        : '1px solid rgba(33, 150, 243, 0.3)',
                    color: theme.palette.text.primary,
                    '& .MuiAlert-icon': {
                        color: theme.palette.info?.main || '#2196f3',
                    },
                };

            default:
                return baseStyles;
        }
    };

    const getIcon = () => {
        switch (type) {
            case 'success':
                return <CheckCircle />;
            case 'error':
                return <Error />;
            case 'warning':
                return <Warning />;
            case 'info':
                return <Info />;
            default:
                return null;
        }
    };

    return (
        <Alert
            severity={type}
            icon={getIcon()}
            sx={{
                ...getAlertStyles(),
                ...sx,
            }}
        >
            {title && (
                <AlertTitle sx={{
                    color: 'inherit',
                    fontWeight: 600,
                    marginBottom: title ? 0.5 : 0,
                }}>
                    {title}
                </AlertTitle>
            )}
            <Box sx={{ color: 'inherit', opacity: 0.9 }}>
                {message}
            </Box>
        </Alert>
    );
};
